/**
 * Dispara session_started con contexto completo (geo, template, PostHog IDs)
 * → /api/track-attribution-event → DB + Slack (#juaperrone vía SLACK_FUNNEL_WEBHOOK_URL).
 *
 * Corre dentro del árbol React (después de boot), no en import-time de App.tsx,
 * para tener PostHog session/distinct id y __TEMPLATE_CONFIG__ del worker.
 */
import { captureAttribution } from "@/lib/captureAttribution";
import { buildSessionStartedMetadata } from "@/lib/buildSessionStartedContext";
import {
  ensureDualMuxSessionRecordingPostHog,
  registerDualMuxMembershipFunnelFromWorkerPostHog,
  trackDualMuxMembershipFunnelSessionPostHog,
} from "@/lib/dualMuxPostHogFunnel";
import { getPostHogCorrelationIdsPostHog } from "@/lib/posthogAnalytics";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  registerVisitorExperienceContextPostHog,
  trackVisitorExperienceResolvedPostHog,
} from "@/lib/visitorExperiencePostHog";
import { useEffect, useRef } from "react";

const SENTINEL = "juaperrone.attribution_session_started.v1";

function postHogReady(): boolean {
  const ph = (window as unknown as { posthog?: { capture?: unknown; get_session_id?: () => string } }).posthog;
  return Boolean(ph?.capture && ph?.get_session_id?.());
}

function waitForPostHog(maxMs = 2500): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (postHogReady()) {
        resolve();
        return;
      }
      if (Date.now() - start >= maxMs) {
        resolve();
        return;
      }
      window.setTimeout(tick, 120);
    };
    tick();
  });
}

export function SessionIngressTracker() {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (typeof window === "undefined") return;

    try {
      if (sessionStorage.getItem(SENTINEL) === "1") {
        firedRef.current = true;
        return;
      }
    } catch {
      /* private mode */
    }

    firedRef.current = true;

    void (async () => {
      await waitForPostHog();

      const pathname = window.location.pathname;
      const tc = getServerTemplateConfig();
      const ph = getPostHogCorrelationIdsPostHog();
      const metadata = buildSessionStartedMetadata();

      ensureDualMuxSessionRecordingPostHog();
      // Full visitor experience (template + dual + cascade) — all surfaces.
      registerVisitorExperienceContextPostHog({
        effectiveTemplate: tc?.templateName,
        dualSurface: tc?.dualSurface,
      });
      trackVisitorExperienceResolvedPostHog({
        effectiveTemplate: tc?.templateName,
        dualSurface: tc?.dualSurface,
      });
      registerDualMuxMembershipFunnelFromWorkerPostHog(tc?.templateName);
      trackDualMuxMembershipFunnelSessionPostHog(metadata);

      captureAttribution("session_started", {
        sessionId: ph.session_id ?? undefined,
        distinctId: ph.distinct_id ?? undefined,
        country: tc?.country?.toUpperCase().slice(0, 2),
        language: tc?.language,
        template: tc?.templateName,
        heroCtaType: tc?.ctaType,
        path: pathname,
        metadata,
      });

      try {
        sessionStorage.setItem(SENTINEL, "1");
      } catch {
        /* ignore */
      }
    })();
  }, []);

  return null;
}
